import {
  CURRENT_CONTENT_VERSION,
  type LearnerProgressSnapshot,
  type TaskAttemptResponse,
} from "@history-sim/api-contracts";
import {
  anonymizeEvents,
  createLearningEvent,
  type LearningEvent,
  type LearningEventType,
} from "@history-sim/domain";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type AppView = "landing" | "map" | "scene" | "report" | "teacher";

export type ReportScope =
  | { readonly kind: "module"; readonly moduleId: string }
  | { readonly kind: "course" };

export const APP_VERSION = "1.0.0";
export const CONTENT_VERSION = CURRENT_CONTENT_VERSION;
export const APP_STATE_STORAGE_VERSION = 6;

export interface ParticipantProfile {
  name: string;
  major: string;
  studentId: string;
  className: string;
}

export interface AccessibilitySettings {
  reducedMotion: boolean;
  highContrast: boolean;
  subtitles: boolean;
  narrationVolume: number;
  ambienceVolume: number;
}

export interface PersistedSceneProgress {
  activeSequenceId: string;
  stage: string;
  completedNodeIds: string[];
  completedTaskIds: string[];
  taskAttemptCounts: Record<string, number>;
  remediation?: {
    sourceTaskId: string;
    required: boolean;
    completed: boolean;
    phase: "steps" | "variant";
    stepIndex: number;
    variantAttempts: number;
    lastMisconceptionCode?: string;
  };
  completed: boolean;
}

interface RecordOptions {
  sceneId?: string;
  taskId?: string;
  durationMs?: number;
}

interface AppState {
  releaseContentVersion: string;
  view: AppView;
  reportScope: ReportScope;
  currentSceneId: string;
  profile: ParticipantProfile | null;
  sessionId: string;
  participantId: string;
  currentMoment: number;
  openedMomentIds: string[];
  completedMomentIds: string[];
  taskAttempts: number;
  taskPassed: boolean;
  evidenceAwarded: boolean;
  completedSceneIds: string[];
  completedTaskIds: string[];
  sceneProgressById: Record<string, PersistedSceneProgress>;
  appliedServerAttemptIds: string[];
  events: LearningEvent[];
  settings: AccessibilitySettings;
  setView: (view: AppView) => void;
  openModuleReport: (moduleId: string) => void;
  openCourseReport: () => void;
  openScene: (sceneId: string) => void;
  startSession: (profile: ParticipantProfile) => void;
  startServerSession: (profile: ParticipantProfile, progress: LearnerProgressSnapshot) => void;
  setMoment: (
    index: number,
    momentId: string,
    metadata?: Record<string, string | number | boolean | null>,
  ) => void;
  completeMoment: (
    momentId: string,
    metadata?: Record<string, string | number | boolean | null>,
  ) => void;
  registerTaskResult: (correct: boolean, optionId: string) => void;
  awardEvidence: () => void;
  completeTask: (taskId: string) => void;
  completeScene: (sceneId: string) => void;
  saveSceneProgress: (
    sceneId: string,
    progress: Partial<Omit<PersistedSceneProgress, "taskAttemptCounts" | "completed">>,
  ) => void;
  registerSceneTaskResult: (sceneId: string, taskId: string, correct: boolean) => number;
  applyServerTaskAttempt: (
    sceneId: string,
    response: TaskAttemptResponse,
  ) => { readonly applied: boolean; readonly attempt: number };
  updateSettings: (settings: Partial<AccessibilitySettings>) => void;
  record: (
    type: LearningEventType,
    payload?: Record<string, string | number | boolean | null>,
    options?: RecordOptions,
  ) => void;
  exportAnonymousEvidence: () => void;
  resetLearning: () => void;
  endSession: () => void;
}

const defaultSettings: AccessibilitySettings = {
  reducedMotion: false,
  highContrast: false,
  subtitles: true,
  narrationVolume: 0.8,
  ambienceVolume: 0.45,
};

const PROFILE_SESSION_KEY = "history-sim-participant-profile-v1";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

function normalizedProfile(value: unknown): ParticipantProfile | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Partial<Record<keyof ParticipantProfile, unknown>>;
  if (
    typeof candidate.name !== "string" ||
    typeof candidate.major !== "string" ||
    typeof candidate.studentId !== "string" ||
    typeof candidate.className !== "string"
  ) {
    return null;
  }
  const profile = {
    name: candidate.name.trim(),
    major: candidate.major.trim(),
    studentId: candidate.studentId.trim(),
    className: candidate.className.trim(),
  };
  if (
    profile.name.length < 1 ||
    profile.name.length > 50 ||
    profile.major.length < 1 ||
    profile.major.length > 100 ||
    !/^[A-Za-z0-9_-]{3,32}$/u.test(profile.studentId) ||
    profile.className.length < 1 ||
    profile.className.length > 80
  ) {
    return null;
  }
  return profile;
}

function readSessionProfile(): ParticipantProfile | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const value = sessionStorage.getItem(PROFILE_SESSION_KEY);
    return value ? normalizedProfile(JSON.parse(value)) : null;
  } catch {
    return null;
  }
}

function writeSessionProfile(profile: ParticipantProfile): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(PROFILE_SESSION_KEY, JSON.stringify(profile));
  } catch {
    // Session storage can be unavailable in privacy-restricted browsers.
  }
}

function clearSessionProfile(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(PROFILE_SESSION_KEY);
  } catch {
    // Session storage can be unavailable in privacy-restricted browsers.
  }
}

const newId = (_prefix: string) => crypto.randomUUID();

function resetContentScopedState(state: Partial<AppState>): Partial<AppState> {
  return {
    ...state,
    releaseContentVersion: CONTENT_VERSION,
    view: "landing",
    reportScope: { kind: "module", moduleId: "M1" },
    currentSceneId: "M1S1",
    sessionId: newId("session"),
    participantId: newId("participant"),
    currentMoment: 0,
    openedMomentIds: [],
    completedMomentIds: [],
    taskAttempts: 0,
    taskPassed: false,
    evidenceAwarded: false,
    completedSceneIds: [],
    completedTaskIds: [],
    sceneProgressById: {},
    appliedServerAttemptIds: [],
    events: [],
  };
}

export function migratePersistedAppState(persistedState: unknown, version: number): AppState {
  let state = persistedState as Partial<AppState>;
  if (version < 2) {
    state = {
      ...state,
      view: state.view === "scene" || state.view === "report" ? "map" : (state.view ?? "landing"),
      currentMoment: 0,
      openedMomentIds: [],
      completedMomentIds: [],
      taskAttempts: 0,
      taskPassed: false,
      evidenceAwarded: false,
      completedSceneIds: [],
      completedTaskIds: [],
      sceneProgressById: {},
      events: [],
      sessionId: newId("session"),
    };
  }
  if (version < 3) {
    state = {
      ...state,
      sceneProgressById: Object.fromEntries(
        Object.entries(state.sceneProgressById ?? {}).map(([sceneId, progress]) => [
          sceneId,
          { ...progress, completedNodeIds: progress.completedNodeIds ?? [] },
        ]),
      ),
    };
  }
  if (version < 4) {
    const existingSessionId = state.sessionId;
    const existingParticipantId = state.participantId;
    state = {
      ...state,
      profile: readSessionProfile(),
      sessionId:
        existingSessionId && UUID_PATTERN.test(existingSessionId)
          ? existingSessionId
          : newId("session"),
      participantId:
        existingParticipantId && UUID_PATTERN.test(existingParticipantId)
          ? existingParticipantId
          : newId("participant"),
    };
  }
  if (version < 5) {
    state = { ...state, appliedServerAttemptIds: [] };
  }
  if (version < APP_STATE_STORAGE_VERSION) {
    state = resetContentScopedState(state);
  }
  return state as AppState;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      releaseContentVersion: CONTENT_VERSION,
      view: "landing",
      reportScope: { kind: "module", moduleId: "M1" },
      currentSceneId: "M1S1",
      profile: readSessionProfile(),
      sessionId: newId("session"),
      participantId: newId("participant"),
      currentMoment: 0,
      openedMomentIds: [],
      completedMomentIds: [],
      taskAttempts: 0,
      taskPassed: false,
      evidenceAwarded: false,
      completedSceneIds: [],
      completedTaskIds: [],
      sceneProgressById: {},
      appliedServerAttemptIds: [],
      events: [],
      settings: defaultSettings,
      setView: (view) => set({ view }),
      openModuleReport: (moduleId) =>
        set({ view: "report", reportScope: { kind: "module", moduleId } }),
      openCourseReport: () => set({ view: "report", reportScope: { kind: "course" } }),
      openScene: (sceneId) => set({ currentSceneId: sceneId, view: "scene" }),
      startSession: (profile) => {
        const nextProfile = normalizedProfile(profile);
        if (!nextProfile) throw new Error("Participant profile is invalid");
        writeSessionProfile(nextProfile);
        set({ profile: nextProfile, view: "map" });
        get().record("session_started", { mode: "standard" });
      },
      startServerSession: (profile, progress) => {
        const nextProfile = normalizedProfile(profile);
        if (!nextProfile) throw new Error("Participant profile is invalid");
        if (progress.contentVersion !== CONTENT_VERSION || progress.authority !== "server") {
          throw new Error("Server progress snapshot is incompatible");
        }
        const completedTaskIds = new Set<string>();
        const completedSceneIds = new Set<string>();
        const sceneProgressById = Object.fromEntries(
          progress.scenes.map((scene) => {
            const taskAttemptCounts: Record<string, number> = {};
            const sceneCompletedTaskIds: string[] = [];
            for (const task of scene.tasks) {
              taskAttemptCounts[task.taskId] = task.attemptCount;
              if (task.passed) {
                sceneCompletedTaskIds.push(task.taskId);
                completedTaskIds.add(task.taskId);
              }
            }
            if (scene.completed) completedSceneIds.add(scene.sceneId);
            return [
              scene.sceneId,
              {
                activeSequenceId: "",
                stage: scene.completed ? "completed" : "sequence",
                completedNodeIds: [...scene.completedNodeIds],
                completedTaskIds: sceneCompletedTaskIds,
                taskAttemptCounts,
                completed: scene.completed,
              } satisfies PersistedSceneProgress,
            ];
          }),
        );
        const t01 = progress.scenes
          .flatMap((scene) => scene.tasks)
          .find((task) => task.taskId === "T01");
        writeSessionProfile(nextProfile);
        set({
          profile: nextProfile,
          view: "map",
          reportScope: { kind: "module", moduleId: "M1" },
          currentSceneId: progress.latestSceneId ?? "M1S1",
          currentMoment: 0,
          openedMomentIds: [...progress.openedMomentIds],
          completedMomentIds: [...progress.completedMomentIds],
          taskAttempts: t01?.attemptCount ?? 0,
          taskPassed: t01?.passed ?? false,
          evidenceAwarded: t01?.passed ?? false,
          completedSceneIds: [...completedSceneIds],
          completedTaskIds: [...completedTaskIds],
          sceneProgressById,
          appliedServerAttemptIds: [],
          events: [],
        });
        get().record("session_started", {
          mode: progress.scenes.length > 0 ? "server_restore" : "standard",
        });
      },
      setMoment: (index, momentId, metadata = {}) => {
        const opened = new Set(get().openedMomentIds);
        opened.add(momentId);
        set({ currentMoment: index, openedMomentIds: [...opened] });
        get().record("archive_opened", { momentId, ...metadata }, { sceneId: "M1S1" });
      },
      completeMoment: (momentId, metadata = {}) => {
        if (get().completedMomentIds.includes(momentId)) return;
        const completed = new Set(get().completedMomentIds);
        completed.add(momentId);
        set({ completedMomentIds: [...completed] });
        get().record(
          "archive_completed",
          { momentId, scope: "moment", ...metadata },
          { sceneId: "M1S1" },
        );
      },
      registerTaskResult: (correct, optionId) => {
        set((state) => ({
          taskAttempts: state.taskAttempts + 1,
          taskPassed: state.taskPassed || correct,
        }));
        const attempt = get().registerSceneTaskResult("M1S1", "T01", correct);
        get().record(
          "task_answered",
          { correct, optionId, attempt },
          { sceneId: "M1S1", taskId: "T01" },
        );
      },
      awardEvidence: () => {
        if (get().evidenceAwarded) return;
        set({ evidenceAwarded: true });
        get().record("evidence_awarded", { evidenceId: "M1_EVIDENCE_UNITY" }, { sceneId: "M1S1" });
      },
      completeTask: (taskId) => {
        const completed = new Set(get().completedTaskIds);
        completed.add(taskId);
        set({ completedTaskIds: [...completed] });
      },
      completeScene: (sceneId) => {
        set((state) => {
          const completed = new Set(state.completedSceneIds);
          completed.add(sceneId);
          const current = state.sceneProgressById[sceneId];
          return {
            completedSceneIds: [...completed],
            sceneProgressById: {
              ...state.sceneProgressById,
              [sceneId]: {
                activeSequenceId: current?.activeSequenceId ?? "",
                stage: current?.stage ?? "completed",
                completedNodeIds: current?.completedNodeIds ?? [],
                completedTaskIds: current?.completedTaskIds ?? [],
                taskAttemptCounts: current?.taskAttemptCounts ?? {},
                ...(current?.remediation ? { remediation: current.remediation } : {}),
                completed: true,
              },
            },
          };
        });
      },
      saveSceneProgress: (sceneId, progress) => {
        set((state) => {
          const current = state.sceneProgressById[sceneId];
          return {
            sceneProgressById: {
              ...state.sceneProgressById,
              [sceneId]: {
                activeSequenceId: progress.activeSequenceId ?? current?.activeSequenceId ?? "",
                stage: progress.stage ?? current?.stage ?? "sequence",
                completedNodeIds: progress.completedNodeIds
                  ? [...new Set(progress.completedNodeIds)]
                  : (current?.completedNodeIds ?? []),
                completedTaskIds: progress.completedTaskIds
                  ? [...new Set(progress.completedTaskIds)]
                  : (current?.completedTaskIds ?? []),
                taskAttemptCounts: current?.taskAttemptCounts ?? {},
                ...(progress.remediation
                  ? { remediation: { ...progress.remediation } }
                  : current?.remediation
                    ? { remediation: current.remediation }
                    : {}),
                completed: current?.completed ?? false,
              },
            },
          };
        });
      },
      registerSceneTaskResult: (sceneId, taskId, correct) => {
        let attempt = 0;
        set((state) => {
          const current = state.sceneProgressById[sceneId];
          attempt = (current?.taskAttemptCounts[taskId] ?? 0) + 1;
          const sceneCompletedTasks = new Set(current?.completedTaskIds ?? []);
          const allCompletedTasks = new Set(state.completedTaskIds);
          if (correct) {
            sceneCompletedTasks.add(taskId);
            allCompletedTasks.add(taskId);
          }
          return {
            completedTaskIds: [...allCompletedTasks],
            sceneProgressById: {
              ...state.sceneProgressById,
              [sceneId]: {
                activeSequenceId: current?.activeSequenceId ?? "",
                stage: current?.stage ?? "sequence",
                completedNodeIds: current?.completedNodeIds ?? [],
                completedTaskIds: [...sceneCompletedTasks],
                taskAttemptCounts: {
                  ...(current?.taskAttemptCounts ?? {}),
                  [taskId]: attempt,
                },
                ...(current?.remediation ? { remediation: current.remediation } : {}),
                completed: current?.completed ?? false,
              },
            },
          };
        });
        return attempt;
      },
      applyServerTaskAttempt: (sceneId, response) => {
        let applied = false;
        let attempt = response.attemptNo;
        set((state) => {
          if (state.appliedServerAttemptIds.includes(response.clientAttemptId)) return {};
          applied = true;
          const current = state.sceneProgressById[sceneId];
          attempt = Math.max(
            (current?.taskAttemptCounts[response.taskId] ?? 0) + 1,
            response.attemptNo,
          );
          const sceneCompletedTasks = new Set(current?.completedTaskIds ?? []);
          const allCompletedTasks = new Set(state.completedTaskIds);
          if (response.correct) {
            sceneCompletedTasks.add(response.taskId);
            allCompletedTasks.add(response.taskId);
          }
          return {
            completedTaskIds: [...allCompletedTasks],
            sceneProgressById: {
              ...state.sceneProgressById,
              [sceneId]: {
                activeSequenceId: current?.activeSequenceId ?? "",
                stage: current?.stage ?? "sequence",
                completedNodeIds: current?.completedNodeIds ?? [],
                completedTaskIds: [...sceneCompletedTasks],
                taskAttemptCounts: {
                  ...(current?.taskAttemptCounts ?? {}),
                  [response.taskId]: attempt,
                },
                ...(current?.remediation ? { remediation: current.remediation } : {}),
                completed: current?.completed ?? false,
              },
            },
            ...(response.taskId === "T01"
              ? {
                  taskAttempts: Math.max(state.taskAttempts, response.attemptNo),
                  taskPassed: state.taskPassed || response.correct,
                }
              : {}),
            appliedServerAttemptIds: [
              ...state.appliedServerAttemptIds.slice(-1_999),
              response.clientAttemptId,
            ],
          };
        });
        if (!applied) return { applied: false, attempt };
        get().record(
          "task_answered",
          {
            correct: response.correct,
            attempt,
            serverAttemptNo: response.attemptNo,
            authority: "server",
          },
          { sceneId, taskId: response.taskId },
        );
        return { applied, attempt };
      },
      updateSettings: (settings) => {
        const nextSettings = { ...get().settings, ...settings };
        set({ settings: nextSettings });
        get().record("quality_changed", {
          reducedMotion: nextSettings.reducedMotion,
          highContrast: nextSettings.highContrast,
          subtitles: nextSettings.subtitles,
        });
      },
      record: (type, payload = {}, options = {}) => {
        const state = get();
        const event = createLearningEvent({
          sessionId: state.sessionId,
          participantId: state.participantId,
          appVersion: APP_VERSION,
          contentVersion: CONTENT_VERSION,
          type,
          payload,
          ...(options.sceneId ? { sceneId: options.sceneId } : {}),
          ...(options.taskId ? { taskId: options.taskId } : {}),
          ...(options.durationMs ? { durationMs: options.durationMs } : {}),
        });
        set((current) => ({ events: [...current.events, event] }));
      },
      exportAnonymousEvidence: () => {
        const state = get();
        const payload = {
          exportedAt: new Date().toISOString(),
          build: APP_VERSION,
          contentVersion: CONTENT_VERSION,
          summary: {
            currentSceneId: state.currentSceneId,
            completedArchives: state.completedMomentIds.length,
            taskAttempts: state.taskAttempts,
            taskPassed: state.taskPassed,
            evidenceAwarded: state.evidenceAwarded,
            completedSceneIds: state.completedSceneIds,
            completedTaskIds: state.completedTaskIds,
            sceneProgressById: state.sceneProgressById,
          },
          events: anonymizeEvents(state.events),
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `history-sim-evidence-${state.sessionId.slice(-8)}.json`;
        anchor.click();
        URL.revokeObjectURL(url);
      },
      resetLearning: () =>
        set({
          view: "map",
          reportScope: { kind: "module", moduleId: "M1" },
          currentSceneId: "M1S1",
          currentMoment: 0,
          openedMomentIds: [],
          completedMomentIds: [],
          taskAttempts: 0,
          taskPassed: false,
          evidenceAwarded: false,
          completedSceneIds: [],
          completedTaskIds: [],
          sceneProgressById: {},
          appliedServerAttemptIds: [],
          events: [],
          sessionId: newId("session"),
        }),
      endSession: () => {
        clearSessionProfile();
        set({
          view: "landing",
          reportScope: { kind: "module", moduleId: "M1" },
          currentSceneId: "M1S1",
          profile: null,
          sessionId: newId("session"),
          participantId: newId("participant"),
          currentMoment: 0,
          openedMomentIds: [],
          completedMomentIds: [],
          taskAttempts: 0,
          taskPassed: false,
          evidenceAwarded: false,
          completedSceneIds: [],
          completedTaskIds: [],
          sceneProgressById: {},
          appliedServerAttemptIds: [],
          events: [],
        });
      },
    }),
    {
      name: "history-sim-session-v1",
      version: APP_STATE_STORAGE_VERSION,
      migrate: migratePersistedAppState,
      merge: (persistedState, currentState) => {
        const profile = readSessionProfile();
        const persisted = persistedState as Partial<AppState>;
        const compatiblePersisted =
          persisted.releaseContentVersion === CONTENT_VERSION
            ? persisted
            : resetContentScopedState(persisted);
        const merged = {
          ...currentState,
          ...compatiblePersisted,
          reportScope:
            compatiblePersisted.reportScope?.kind === "course" ||
            (compatiblePersisted.reportScope?.kind === "module" &&
              typeof compatiblePersisted.reportScope.moduleId === "string")
              ? compatiblePersisted.reportScope
              : currentState.reportScope,
          profile,
        };
        if (profile) return merged;
        return {
          ...merged,
          view: "landing" as const,
          reportScope: { kind: "module" as const, moduleId: "M1" },
          currentSceneId: "M1S1",
          sessionId: newId("session"),
          participantId: newId("participant"),
          currentMoment: 0,
          openedMomentIds: [],
          completedMomentIds: [],
          taskAttempts: 0,
          taskPassed: false,
          evidenceAwarded: false,
          completedSceneIds: [],
          completedTaskIds: [],
          sceneProgressById: {},
          appliedServerAttemptIds: [],
          events: [],
        };
      },
      partialize: (state) => ({
        releaseContentVersion: state.releaseContentVersion,
        view: state.view,
        reportScope: state.reportScope,
        currentSceneId: state.currentSceneId,
        sessionId: state.sessionId,
        participantId: state.participantId,
        currentMoment: state.currentMoment,
        openedMomentIds: state.openedMomentIds,
        completedMomentIds: state.completedMomentIds,
        taskAttempts: state.taskAttempts,
        taskPassed: state.taskPassed,
        evidenceAwarded: state.evidenceAwarded,
        completedSceneIds: state.completedSceneIds,
        completedTaskIds: state.completedTaskIds,
        sceneProgressById: state.sceneProgressById,
        appliedServerAttemptIds: state.appliedServerAttemptIds,
        events: state.events,
        settings: state.settings,
      }),
    },
  ),
);
